import { MultipleSelectComponent } from 'src/app/components/multiple-select/multiple-select.component';
import { TableSqlBuilderHelper } from 'src/app/components/table-dashboard-item/sql-builder-helper';
import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { DatePickerSqlTokensCompiler } from 'src/app/utils/datasource-sql-map/date-picker-sql-tokens-compiler';
import { multipleSelectComponentCompileSQLTokens } from 'src/app/utils/datasource-sql-map/utils';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { buildSplitGroupSQL, buildWhereSQL } from 'src/app/utils/query/sql';
import { sqlDateTrunc } from 'src/app/utils/sql';

export class BiVizDashboardCampaignsDatasource {
    public static sqlMap: IDatasourceSQLMap = {
        dataset: 'bi_viz',
        tableId: 'dashboard_campaigns',
        columns: {
            FIREBASE_FIRST_OPEN_DATE: {
                columnId: 'FIREBASE_FIRST_OPEN_DATE',
                columnSQL: 'first_session_date',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'first_session_date',
                        state
                    );
                },
            },
            FIREBASE_APP_STORE: {
                columnId: 'FIREBASE_APP_STORE',
                columnSQL: 'first_store',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'first_store',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_TIER: {
                columnId: 'FIREBASE_COUNTRY_TIER',
                columnSQL: 'country_tier_label',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'country_tier_label',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY: {
                columnId: 'FIREBASE_COUNTRY',
                columnSQL: 'unified_country',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_country',
                        state
                    );
                },
            },
            ATTRIBUTION_TRAFFIC_SOURCE: {
                columnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
                columnSQL: 'unified_traffic',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_traffic',
                        state
                    );
                },
            },
            ATTRIBUTION_NETWORK_NAME: {
                columnId: 'ATTRIBUTION_NETWORK_NAME',
                columnSQL: 'unified_network',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_network',
                        state
                    );
                },
            },
            ATTRIBUTION_CAMPAIGN_ACTIVE: {
                columnId: 'ATTRIBUTION_CAMPAIGN_ACTIVE',
                columnSQL: 'active_campaign',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'active_campaign',
                        state,
                        MultipleSelectComponent.toBooleanSQL
                    );
                },
            },
            ATTRIBUTION_CAMPAIGN_NAME: {
                columnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                columnSQL: 'unified_campaign',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_campaign',
                        state
                    );
                },
            },
            ATTRIBUTION_AD: {
                columnId: 'ATTRIBUTION_AD',
                columnSQL: 'unified_ad',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_ad',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_LENGTH_S: {
                columnId: 'ATTRIBUTION_AD_LENGTH_S',
                columnSQL: 'unified_ad_dim.length_s',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_ad_dim.length_s',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_SIZE: {
                columnId: 'ATTRIBUTION_AD_SIZE',
                columnSQL: 'unified_ad_dim.size',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_ad_dim.size',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_ASPECT_RATIO: {
                columnId: 'ATTRIBUTION_AD_ASPECT_RATIO',
                columnSQL: 'unified_ad_dim.aspect_ratio',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_ad_dim.aspect_ratio',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_TYPE: {
                columnId: 'ATTRIBUTION_AD_TYPE',
                columnSQL: 'unified_ad_dim.type',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_ad_dim.type',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_THEME: {
                columnId: 'ATTRIBUTION_AD_THEME',
                columnSQL: 'unified_ad_dim.theme',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_ad_dim.theme',
                        state
                    );
                },
            },
            ATTRIBUTION_AD_VERSION: {
                columnId: 'ATTRIBUTION_AD_VERSION',
                columnSQL: 'unified_ad_dim.version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_ad_dim.version',
                        state
                    );
                },
            },
            ATTRIBUTION_ADSET: {
                columnId: 'ATTRIBUTION_ADSET',
                columnSQL: 'unified_adset',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_adset',
                        state
                    );
                },
            },
        },
    };

    // compile sql statements specific for this datasource
    public static compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) {
        const dateTrunc = (alias: string = 'date') =>
            queryContextSettings.dateAggregation
                ? sqlDateTrunc(alias, queryContextSettings.dateAggregation)
                : alias;

        const sqlTokensArray: {
            id: DatasourceColumnId;
            compiledSqlTokens: Partial<ICompiledSQLTokens>;
        }[] = supportedDatasourceColumns.map((dsCol) => {
            return {
                id: dsCol.id,
                compiledSqlTokens: this.sqlMap.columns[dsCol.id]!
                    .compileSQLTokens!(dsCol.state, options),
            };
        });

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokens: ICompiledSQLTokens =
            mergeSqlTokensArray(sqlTokensArray);

        const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);
        const splitGroup = splitSelect ? 'split_group' : '';
        const splitGroupBy = splitSelect ? 'split_group, ' : '';

        // create splitSelect in the form: dsCol.columnSQL AS dsCol.id
        const splitSelectAsSeparateColumns =
            TableSqlBuilderHelper.getSplitSelect(sqlTokensArray);

        // create splitGroupBy in the form: dsCol.id
        const splitGroupByAsSeparateColumns =
            TableSqlBuilderHelper.getSplitGroupBy(sqlTokensArray);

        // // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const mergedSqlTokens: ICompiledSQLTokens = mergeSqlTokensArray(
            sqlTokensArray.map((sqlToken) => sqlToken.compiledSqlTokens)
        );

        // CUSTOM FILTER FOR THIS TABLE
        // sqlTokens.where.push(`validation_flag IS NULL`);

        // unnest app_version_activity if app_version is present in splits or filters
        // TODO to powinno być rozwiązane na poziomie datasource, gdzie compileSQL zwróci atrybut join, z treścią `, UNNEST...`

        // const splitGroupBy = splitSelect ? 'split_group, ' : '';
        const where: string = buildWhereSQL([
            ...mergedSqlTokens.where,
            ...(customCompiledSqlTokens?.where ?? []),
        ]);

        return {
            dateTrunc,
            splitSelect,
            splitSelectAsSeparateColumns,
            /** `'split_group' or '' */
            splitGroup,
            /** `'split_group, ' or '' */
            splitGroupBy,
            splitGroupByAsSeparateColumns,
            where,
        };
    }
}
